import React, { useState, useEffect } from 'react';
import { BarChart3, DollarSign, Target, TrendingUp } from 'lucide-react';
import { analyticsAPI, recipesAPI, loadsAPI } from '../services/api';

const Analytics = () => {
  const [costByCaliber, setCostByCaliber] = useState([]);
  const [costByFirearm, setCostByFirearm] = useState([]);
  const [brassStatus, setBrassStatus] = useState([]);
  const [loading, setLoading] = useState(true);
  const [recipeAvgById, setRecipeAvgById] = useState({});

  const toNumber = (v, fallback = 0) => {
    const n = Number(v);
    return Number.isFinite(n) ? n : fallback;
  };

  useEffect(() => {
    loadAnalytics();
  }, []);

  const loadAnalytics = async () => {
    try {
      setLoading(true);
      const [caliberRes, firearmRes, brassRes, recipesRes, loadsRes] = await Promise.all([
        analyticsAPI.getCostByCaliber(),
        analyticsAPI.getCostByFirearm(),
        analyticsAPI.getBrassStatus(),
        recipesAPI.getAll(),
        loadsAPI.getAll()
      ]);
      setCostByCaliber(Array.isArray(caliberRes.data) ? caliberRes.data : []);
      setCostByFirearm(Array.isArray(firearmRes.data) ? firearmRes.data : []);
      setBrassStatus(Array.isArray(brassRes.data) ? brassRes.data : []);
      // Build map of recipeId -> average CPR from recipes endpoint
      try {
        // Primary source: derive average CPR per recipe from current loads
        const byRecipe = {};
        const loads = Array.isArray(loadsRes.data) ? loadsRes.data : [];
        loads.forEach((l) => {
          const rid = l?.recipe?.id;
          if (!rid) return;
          const qty = Number(l.quantity_created) || 0;
          const total = Number(l.total_cost);
          const fallbackTotal = (!isNaN(total) && total > 0) ? total : ((Number(l.cost_per_round) || 0) * qty);
          if (!byRecipe[rid]) byRecipe[rid] = { total: 0, qty: 0 };
          byRecipe[rid].total += (isNaN(fallbackTotal) ? 0 : fallbackTotal);
          byRecipe[rid].qty += qty;
        });
        const avgMap = {};
        Object.entries(byRecipe).forEach(([rid, agg]) => {
          avgMap[rid] = agg.qty > 0 ? (agg.total / agg.qty) : null;
        });
        // Fallback/augment with backend-provided recipe averages if present
        const list = Array.isArray(recipesRes.data) ? recipesRes.data : [];
        list.forEach((r) => {
          const rid = r && r.id;
          if (!rid) return;
          const avg = (typeof r.global_average_cost_per_round === 'number')
            ? r.global_average_cost_per_round
            : (typeof r.average_cost_per_round === 'number' ? r.average_cost_per_round : null);
          if (avg != null) avgMap[rid] = Number(avg);
        });
        setRecipeAvgById(avgMap);
      } catch {}
    } catch (error) {
      console.error('Error loading analytics:', error);
      setCostByCaliber([]);
      setCostByFirearm([]);
      setBrassStatus([]);
    } finally {
      setLoading(false);
    }
  };

  const totalSpent = costByCaliber.reduce((sum, item) => sum + toNumber(item.total_cost), 0);
  const totalRounds = costByCaliber.reduce((sum, item) => sum + toNumber(item.total_rounds), 0);
  const averageCostPerRound = totalRounds > 0 ? totalSpent / totalRounds : 0;

  // Backend now sends clean data:
  // - purchased_cases: raw purchased count
  // - total_cases: purchased - exhausted
  // - available_cases: empty cases ready to load (total - active)
  // - active_cases: cases currently in loaded ammo
  // - exhausted_cases: cases removed/destroyed
  const normalizedBrass = brassStatus.map((item) => {
    const purchased_cases = toNumber(item.purchased_cases);
    const exhausted_cases = toNumber(item.exhausted_cases);
    const total_cases = toNumber(item.total_cases);
    const available_cases = toNumber(item.available_cases);
    const active_cases = toNumber(item.active_cases);
    const total_firings = toNumber(item.total_firings);
    return {
      ...item,
      purchased_cases,
      exhausted_cases,
      total_firings,
      total_cases,
      available_cases,
      active_loaded_cases: active_cases,
      average_firings_per_case: toNumber(item.average_firings_per_case ?? (total_cases > 0 ? total_firings / total_cases : 0)),
    };
  });

  const totalBrassCases = normalizedBrass.reduce((sum, item) => sum + toNumber(item.total_cases), 0);
  const totalBrassFirings = normalizedBrass.reduce((sum, item) => sum + toNumber(item.total_firings), 0);
  const averageFiringsPerCase = totalBrassCases > 0 ? totalBrassFirings / totalBrassCases : 0;
  const totalAvailableCases = normalizedBrass.reduce((sum, item) => sum + toNumber(item.available_cases), 0);
  const totalActiveLoadedCases = normalizedBrass.reduce((sum, item) => sum + toNumber(item.active_loaded_cases), 0);
  const totalExhaustedCases = normalizedBrass.reduce((sum, item) => sum + toNumber(item.exhausted_cases), 0);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-gray-900">Analytics</h1>
        <p className="text-gray-600 mt-2">Cost analysis and performance insights</p>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="card p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Total Spent</p>
              <p className="text-2xl font-bold text-red-600">${toNumber(totalSpent).toFixed(2)}</p>
            </div>
            <div className="p-3 bg-red-100 rounded-full">
              <DollarSign className="h-6 w-6 text-red-600" />
            </div>
          </div>
        </div>

        <div className="card p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Total Rounds</p>
              <p className="text-2xl font-bold text-blue-600">{toNumber(totalRounds).toLocaleString()}</p>
            </div>
            <div className="p-3 bg-blue-100 rounded-full">
              <Target className="h-6 w-6 text-blue-600" />
            </div>
          </div>
        </div>

        <div className="card p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Avg Cost/Round</p>
              <p className="text-2xl font-bold text-green-600">${toNumber(averageCostPerRound).toFixed(2)}</p>
            </div>
            <div className="p-3 bg-green-100 rounded-full">
              <TrendingUp className="h-6 w-6 text-green-600" />
            </div>
          </div>
        </div>

        <div className="card p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Calibers Tracked</p>
              <p className="text-2xl font-bold text-purple-600">{costByCaliber.length}</p>
            </div>
            <div className="p-3 bg-purple-100 rounded-full">
              <BarChart3 className="h-6 w-6 text-purple-600" />
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Cost by Caliber */}
        <div className="card p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Cost Analysis by Caliber</h3>
          <div className="space-y-4">
            {costByCaliber.map((item, index) => {
              const totalCost = toNumber(item.total_cost);
              const totalRoundsItem = toNumber(item.total_rounds);
              const maxCost = Math.max(...costByCaliber.map(c => toNumber(c.total_cost)), 0);
              const barWidth = maxCost > 0 ? (totalCost / maxCost) * 100 : 0;
              const cpr = totalRoundsItem > 0 ? totalCost / totalRoundsItem : 0;

              return (
                <div key={index} className="space-y-2">
                  <div className="flex justify-between items-center">
                    <span className="font-medium text-gray-900">{item.caliber || 'Unknown'}</span>
                    <span className="text-sm text-gray-600">${toNumber(totalCost).toFixed(2)}</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div
                      className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                      style={{ width: `${barWidth}%` }}
                    ></div>
                  </div>
                  <div className="flex justify-between text-xs text-gray-500">
                    <span>{toNumber(totalRoundsItem).toLocaleString()} rounds</span>
                    <span>${toNumber(cpr).toFixed(2)}/round</span>
                  </div>
                </div>
              );
            })}
            {costByCaliber.length === 0 && (
              <p className="text-gray-500 text-center py-8">No cost data available</p>
            )}
          </div>
        </div>

        {/* Cost by Firearm (fired rounds) */}
        <div className="card p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Cost Analysis by Firearm (Fired)</h3>
          <div className="space-y-4">
            {costByFirearm.map((item, index) => {
              // Compute total using recipe average CPRs if possible
              const computedTotal = Array.isArray(item.recipes) && item.recipes.length > 0
                ? item.recipes.reduce((sum, r) => {
                    const rid = r.recipe_id || r.id;
                    const rf = toNumber(r.rounds_fired);
                    const avgCpr = (rid && typeof recipeAvgById[rid] === 'number') ? recipeAvgById[rid] : null;
                    if (avgCpr != null) return sum + (rf * avgCpr);
                    // Fallback: use r.total_cost_fired if provided
                    const rc = toNumber(r.total_cost_fired);
                    return sum + rc;
                  }, 0)
                : toNumber(item.total_cost_fired ?? item.total_cost);
              const totalCost = computedTotal;
              const totalRoundsItem = toNumber(item.rounds_fired ?? item.total_rounds);
              const maxCost = Math.max(...costByFirearm.map(f => {
                const fr = Array.isArray(f.recipes) && f.recipes.length > 0
                  ? f.recipes.reduce((sum, r) => {
                      const rid = r.recipe_id || r.id;
                      const rf = toNumber(r.rounds_fired);
                      const avgCpr = (rid && typeof recipeAvgById[rid] === 'number') ? recipeAvgById[rid] : null;
                      if (avgCpr != null) return sum + (rf * avgCpr);
                      return sum + toNumber(r.total_cost_fired);
                    }, 0)
                  : toNumber((f.total_cost_fired ?? f.total_cost));
                return fr;
              }), 0);
              const barWidth = maxCost > 0 ? (totalCost / maxCost) * 100 : 0;
              const cpr = totalRoundsItem > 0 ? totalCost / totalRoundsItem : 0;

              return (
                <div key={index} className="space-y-2">
                  <div className="flex justify-between items-center">
                    <div>
                      <span className="font-medium text-gray-900">{item.firearm || 'Unknown'}</span>
                      <p className="text-sm text-gray-600">{item.caliber || '—'}</p>
                    </div>
                    <span className="text-sm text-gray-600">${toNumber(totalCost).toFixed(2)}</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div
                      className="bg-green-600 h-2 rounded-full transition-all duration-300"
                      style={{ width: `${barWidth}%` }}
                    ></div>
                  </div>
                  <div className="flex justify-between text-xs text-gray-500">
                    <span>{toNumber(totalRoundsItem).toLocaleString()} rounds fired</span>
                    <span>${toNumber(cpr).toFixed(2)}/fired round</span>
                  </div>

                  {/* Per-recipe breakdown */}
                  {Array.isArray(item.recipes) && item.recipes.length > 0 && (
                    <div className="mt-2 space-y-1">
                      {item.recipes.map((r, i) => {
                        const rid = r.recipe_id || r.id;
                        const rf = toNumber(r.rounds_fired);
                        const avgCpr = (rid && typeof recipeAvgById[rid] === 'number') ? recipeAvgById[rid] : null;
                        const lineTotal = (avgCpr != null) ? (rf * avgCpr) : toNumber(r.total_cost_fired);
                        return (
                          <div key={i} className="flex justify-between text-xs text-gray-600">
                            <span className="truncate pr-2">{r.recipe_name}</span>
                            <span>{rf.toLocaleString()} rd • ${toNumber(lineTotal).toFixed(2)}</span>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
            {costByFirearm.length === 0 && (
              <p className="text-gray-500 text-center py-8">No firearm cost data available</p>
            )}
          </div>
        </div>
      </div>

      {/* Brass Status */}
      <div className="card p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Brass Case Status & Lifecycle</h3>
        
        {normalizedBrass.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-6">
            <div className="text-center p-4 bg-yellow-50 rounded-lg">
              <div className="text-2xl font-bold text-yellow-600">{toNumber(totalBrassCases).toLocaleString()}</div>
              <div className="text-sm text-yellow-600">Total Cases</div>
            </div>
            <div className="text-center p-4 bg-green-50 rounded-lg">
              <div className="text-2xl font-bold text-green-600">{toNumber(totalAvailableCases).toLocaleString()}</div>
              <div className="text-sm text-green-600">Available Cases</div>
            </div>
            <div className="text-center p-4 bg-blue-50 rounded-lg">
              <div className="text-2xl font-bold text-blue-600">{toNumber(totalActiveLoadedCases).toLocaleString()}</div>
              <div className="text-sm text-blue-600">Active Cases</div>
            </div>
            <div className="text-center p-4 bg-red-50 rounded-lg">
              <div className="text-2xl font-bold text-red-600">{toNumber(totalExhaustedCases).toLocaleString()}</div>
              <div className="text-sm text-red-600">Exhausted Cases</div>
            </div>
            <div className="text-center p-4 bg-purple-50 rounded-lg">
              <div className="text-2xl font-bold text-purple-600">{toNumber(averageFiringsPerCase).toFixed(1)}</div>
              <div className="text-sm text-purple-600">Avg Firings/Case</div>
            </div>
          </div>
        )}

        <div className="space-y-4">
          {normalizedBrass.map((item, index) => {
            const usagePercentage = item.total_cases > 0 ? (toNumber(item.exhausted_cases) / toNumber(item.total_cases)) * 100 : 0;
            return (
              <div key={index} className="border border-gray-200 rounded-lg p-4">
                <div className="flex justify-between items-center mb-3">
                  <div>
                    <h4 className="font-medium text-gray-900">
                      {(() => {
                        const hs = item.head_stamp;
                        if (hs) return hs;
                        const b = (item.brand || '').trim();
                        const m = (item.model || '').trim();
                        if (!m && b) return b;
                        if (m && b && m.toLowerCase().includes(b.toLowerCase())) return m;
                        return [b, m].filter(Boolean).join(' ');
                      })()}
                    </h4>
                    <p className="text-sm text-gray-600">{item.caliber || '—'}</p>
                  </div>
                  <div className="text-right">
                    <div className="text-lg font-semibold text-gray-900">
                      {toNumber(item.average_firings_per_case).toFixed(1)} avg firings
                    </div>
                    <div className="text-sm text-gray-600">
                      {toNumber(item.total_firings).toLocaleString()} total firings
                    </div>
                  </div>
                </div>
                
                <div className="grid grid-cols-4 gap-4 text-sm">
                  <div className="text-center">
                    <div className="font-medium text-yellow-600">{toNumber(item.total_cases).toLocaleString()}</div>
                    <div className="text-gray-600">Total Cases</div>
                  </div>
                  <div className="text-center">
                    <div className="font-medium text-green-600">{toNumber(item.available_cases).toLocaleString()}</div>
                    <div className="text-gray-600">Available Cases</div>
                  </div>
                  <div className="text-center">
                    <div className="font-medium text-blue-600">{toNumber(item.active_loaded_cases).toLocaleString()}</div>
                    <div className="text-gray-600">Active Cases</div>
                  </div>
                  <div className="text-center">
                    <div className="font-medium text-red-600">{toNumber(item.exhausted_cases).toLocaleString()}</div>
                    <div className="text-gray-600">Exhausted Cases</div>
                  </div>
                </div>
                
                <div className="mt-3">
                  <div className="flex justify-between text-xs text-gray-600 mb-1">
                    <span>Case Lifecycle Progress</span>
                    <span>{toNumber(usagePercentage).toFixed(1)}% exhausted</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div
                      className="bg-red-600 h-2 rounded-full transition-all duration-300"
                      style={{ width: `${usagePercentage}%` }}
                    ></div>
                  </div>
                </div>
              </div>
            );
          })}
          
          {normalizedBrass.length === 0 && (
            <div className="text-center py-8">
              <div className="text-4xl mb-4">🥉</div>
              <p className="text-gray-500">No brass case data available</p>
              <p className="text-sm text-gray-400 mt-2">
                Brass tracking begins when you record purchases and firing sessions
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Analytics;